// CameraMovement.tsx
import usePanoramaStore from '@/store/panorama';
import useEquirectangularStore from '@/store/viewer';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const CameraMovement: React.FC = () => {
  const { camera } = useThree();
  const targetPosition = useRef(new THREE.Vector3());
  const speed = 0.1;
  const isMoving = useRef(false);
  const startViewerKeyRef = useRef<string | null>(null);
  const currentPosition = usePanoramaStore((state) => state.currentPosition);
  const moveByWidget = usePanoramaStore((state) => state.moveByWidget);
  const setMoveByWidget = usePanoramaStore((state) => state.setMoveByWidget);
  const viewers = useEquirectangularStore((state) => state.viewers);
  const currentViewerKey = useEquirectangularStore(
    (state) => state.currentViewerKey
  );
  const setCurrentViewerKey = useEquirectangularStore(
    (state) => state.setCurrentViewerKey
  );
  const updateViewer = useEquirectangularStore((state) => state.updateViewer);

  const finalizeCameraMove = (viewerKey: string) => {
    camera.position.copy(targetPosition.current);
    isMoving.current = false;

    const startViewerKey = startViewerKeyRef.current;
    if (startViewerKey && startViewerKey !== viewerKey) {
      updateViewer(startViewerKey, { visible: false, radius: 0.1, opacity: 0 });
    }

    setCurrentViewerKey(viewerKey);
    updateViewer(viewerKey, { visible: true, radius: 1, opacity: 1 });
    setMoveByWidget(false);
  };

  useEffect(() => {
    if (moveByWidget) {
      targetPosition.current.set(
        currentPosition[0],
        currentPosition[1],
        currentPosition[2]
      );
      isMoving.current = true;
      startViewerKeyRef.current = currentViewerKey;

      const matchingViewerKey = Object.entries(viewers).find(
        ([_key, viewer]) =>
          Array.isArray(viewer.position) &&
          viewer.position.length === 3 &&
          viewer.position.every((pos) => typeof pos === 'number') &&
          viewer.position.every((pos, index) => pos === currentPosition[index])
      )?.[0];

      if (matchingViewerKey) {
        const distance = targetPosition.current.distanceTo(camera.position);

        updateViewer(matchingViewerKey, {
          visible: true,
          radius: distance * 2,
          opacity: 1,
        });
        updateViewer(startViewerKeyRef.current!, {
          visible: true,
          radius: distance * 2,
          opacity: 1,
        });
      }
    }
  }, [currentPosition]);

  useFrame(() => {
    if (isMoving.current) {
      camera.position.lerp(targetPosition.current, speed);

      if (camera.position.distanceTo(targetPosition.current) < 0.01) {
        camera.position.copy(targetPosition.current);
        isMoving.current = false;

        const matchingViewerKey = Object.entries(viewers).find(
          ([_key, viewer]) =>
            Array.isArray(viewer.position) &&
            viewer.position.length === 3 &&
            viewer.position.every((pos) => typeof pos === 'number') &&
            viewer.position.every(
              (pos, index) => pos === currentPosition[index]
            )
        )?.[0];

        if (matchingViewerKey) {
          finalizeCameraMove(matchingViewerKey);
        }
      }
    }
  });

  return null;
};

export default CameraMovement;
